import { TGetRenewVisaDocumentListOutputDto } from "@/api/services/main/renew-visa/dtos/get-renew-visa-document-list.dto";
import { useMakeRenewVisaColumns } from "@/features/renew-visa/hook/useMakeRenewVisaColumns";
import { useTable } from "@/hooks/useTable";
import { background, button } from "@/theme/colors";
import { RENEW_VISA_DOCUMENT_STATUS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { RowSelectionState, Updater } from "@tanstack/table-core";
import { MRT_RowSelectionState } from "material-react-table";
import { useMemo, useState } from "react";

type TUseRenewVisaSelectionTableProps = {
  rows: TGetRenewVisaDocumentListOutputDto[];
};

export const useRenewVisaSelectionTable = ({ rows }: TUseRenewVisaSelectionTableProps) => {
  const columns = useMakeRenewVisaColumns();

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const [selectedCompanyName, setSelectedCompanyName] = useState<string | null>(null);
  const [selectedCompanySpecificIndustryName, setSelectedCompanySpecificIndustryName] = useState<string | null>(null);
  const [selectedResidenceStatus, setSelectedResidenceStatus] = useState<string | null>(null);

  const rowSelectionKeys = useMemo(() => Object.keys(rowSelection).map((item) => JSON.parse(item)?.id), [rowSelection]);

  const rowSelections = useMemo(() => Object.keys(rowSelection).map((item) => JSON.parse(item)), [rowSelection]);

  const { table } = useTable({
    columns,
    rows,
    getRowId: (row) => {
      return JSON.stringify(row);
    },
    state: { rowSelection },
    onRowSelectionChange: (newSelection: Updater<RowSelectionState>) => {
      const updatedSelection = typeof newSelection === "function" ? newSelection(rowSelection) : newSelection;
      setRowSelection(updatedSelection);
      const selectedRows = Object.keys(updatedSelection);
      if (selectedRows.length > 0) {
        const selectedRow = JSON.parse(selectedRows[0]);
        if (selectedRow) {
          setSelectedCompanyName(selectedRow.companyName);
          setSelectedCompanySpecificIndustryName(selectedRow.companySpecificIndustryName ?? null);
          setSelectedResidenceStatus(selectedRow.employeeResidenceStatus ?? null);
        } else {
          setSelectedCompanyName(null);
          setSelectedCompanySpecificIndustryName(null);
          setSelectedResidenceStatus(null);
        }
      } else {
        setSelectedCompanyName(null);
        setSelectedCompanySpecificIndustryName(null);
        setSelectedResidenceStatus(null);
      }
    },
    pinningColumns: ["action"],
    enableRowSelection: (row) =>
      selectedCompanyName && selectedCompanySpecificIndustryName && selectedResidenceStatus
        ? row.original.companyName === selectedCompanyName &&
          row.original.companySpecificIndustryName === selectedCompanySpecificIndustryName &&
          row.original.employeeResidenceStatus === selectedResidenceStatus &&
          row.original.status === RENEW_VISA_DOCUMENT_STATUS.NOT_CREATED &&
          row.original.employeeResidenceStatus !== "特定技能2号"
        : row.original.status === RENEW_VISA_DOCUMENT_STATUS.NOT_CREATED && row.original.employeeResidenceStatus !== "特定技能2号",
    muiTableBodyRowProps: ({ row }) => ({
      sx:
        ![RENEW_VISA_DOCUMENT_STATUS.COMPLETE, RENEW_VISA_DOCUMENT_STATUS.WITHDRAWN, RENEW_VISA_DOCUMENT_STATUS.REJECTED].includes(
          row.original.status,
        ) && dayjs(row.original.expectSubmitDate).isBefore(dayjs())
          ? { backgroundColor: `${background.error} !important`, "&:hover": `${background.error} !important` }
          : undefined,
    }),
    muiTableHeadCellProps: ({ column }) => ({
      ...(column.id === "mrt-row-select"
        ? {
            sx: {
              pointerEvents: !selectedCompanyName || !selectedCompanySpecificIndustryName || !selectedResidenceStatus ? "none" : "auto",
              "& .MuiCheckbox-root":
                !selectedCompanyName || !selectedCompanySpecificIndustryName || !selectedResidenceStatus
                  ? { color: `${button.disable} !important` }
                  : undefined,
            },
          }
        : {}),
    }),
  });

  return { table, rowSelectionKeys, rowSelections };
};
